import { Icon, Text } from "@blockdaemon-internal/precision-ui";
import { Group, Tooltip, UnstyledButton } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import type { ComponentProps } from "react";

interface AddressProps extends Omit<ComponentProps<typeof Text>, "children"> {
  address: string | undefined;
  sliceStart?: number;
  sliceEnd?: number;
  showCopyButton?: boolean;
  /** Additional props for the Text component */
  size?: string;
  ff?: string;
  tooltipPosition?: "left" | "right" | "top" | "bottom";
}

export function Address({
  address,
  sliceStart = 6,
  sliceEnd = 4,
  showCopyButton = true,
  size,
  ff,
  tooltipPosition = "left",
  ...textProps
}: AddressProps) {
  const clipboard = useClipboard({ timeout: 1000 });

  // Return null if no valid address is provided
  if (!address || address.trim() === "") {
    return null;
  }

  const slicedAddress = `${address.slice(0, sliceStart)}...${address.slice(-sliceEnd)}`;

  const handleCopy = () => {
    clipboard.copy(address);
  };

  return (
    <Tooltip label={clipboard.copied ? "Copied!" : "Copy"} position={tooltipPosition} withinPortal>
      <Group
        spacing="0.3rem"
        noWrap
        onClick={handleCopy}
        sx={{
          cursor: "pointer",
          "&:hover": {
            opacity: 0.8,
          },
        }}
      >
        <Text
          ff="monospace"
          size="sm"
          sx={(theme) => ({
            color:
              theme.colorScheme === "dark"
                ? (textProps.color ?? theme.colors[theme.primaryColor][1])
                : (textProps.color ?? theme.colors[theme.primaryColor][5]),
          })}
          {...textProps}
        >
          {slicedAddress}
        </Text>
        {showCopyButton && (
          <UnstyledButton
            aria-label={clipboard.copied ? "Copied!" : "Copy"}
            fz="14px"
            mt="-1px"
            sx={(theme) => ({
              color:
                theme.colorScheme === "dark"
                  ? (textProps.color ?? theme.colors[theme.primaryColor][1])
                  : (textProps.color ?? theme.colors[theme.primaryColor][5]),
            })}
          >
            <Icon icon={clipboard.copied ? "check" : "content_copy"} />
          </UnstyledButton>
        )}
      </Group>
    </Tooltip>
  );
}
